/**
 * Catalog Sync — how a failed download reads (RRC plan PR 3).
 *
 * Corbin: "we need to show when the file fails to download". The newest run of an agency
 * says which file failed and why; this turns that into the card's one line, and labels
 * each source file (RRC has four).
 */

import type { LastSyncRun, SourceFileStatus } from './hooks';

/**
 * The agencies whose sync is built: a mirror of shared's CATALOG_SYNC_BUILT_AGENCIES (web-admin
 * imports shared by subpath only), pinned to it by download-status.test.ts.
 */
export const SYNCING_AGENCIES = ['OCC', 'RRC'] as const;

export interface DownloadFailure {
  readonly file: string;
  readonly reason: string;
  /** When the failed run ended (or started, if it never recorded an end). */
  readonly at: string | null;
}

const DOWNLOAD_KINDS = new Set(['DOWNLOAD_FAILED', 'SOURCE_PAGE_CHANGED']);

/** The newest run's failed download, or null when its download did not fail. */
export function downloadFailure(
  agency: string,
  run: LastSyncRun | null | undefined,
): DownloadFailure | null {
  if (run == null || run.status !== 'FAILED' || !DOWNLOAD_KINDS.has(run.errorKind ?? ''))
    return null;
  const file = run.failedFile ?? 'source file';
  let reason: string;
  if (run.errorKind === 'SOURCE_PAGE_CHANGED') {
    reason = `${agency} download page changed`;
  } else if (run.httpStatus !== null) {
    reason = `the server answered HTTP ${String(run.httpStatus)}`;
  } else {
    reason = 'the server could not be reached';
  }
  if (run.downloadAttempts !== null && run.downloadAttempts > 1) {
    reason += ` (${String(run.downloadAttempts)} tries)`;
  }
  return { file, reason, at: run.completedAt ?? run.startedAt };
}

/**
 * The files a run reports, with the RRC's county zips (`wellNNN.zip`, ~250) shown as ONE line,
 * so the page lists the four Texas datasets. The run keeps a line per zip (the next run's
 * baseline is per file); this is display only.
 */
const COUNTY_ZIP = /^well\d{3}\.zip$/i;

export function groupSourceFiles(files: readonly SourceFileStatus[]): SourceFileStatus[] {
  const zips = files.filter((f) => COUNTY_ZIP.test(f.name));
  const rest = files.filter((f) => !COUNTY_ZIP.test(f.name));
  if (zips.length === 0) return [...rest];
  const outcome: SourceFileStatus['outcome'] = zips.some((z) => z.outcome === 'FAILED')
    ? 'FAILED'
    : zips.some((z) => z.outcome === 'DOWNLOADED')
      ? 'DOWNLOADED'
      : 'UNCHANGED';
  return [...rest, { name: `well layers (${String(zips.length)} county zips)`, outcome }];
}

export function fileOutcomeLabel(outcome: SourceFileStatus['outcome']): string {
  switch (outcome) {
    case 'DOWNLOADED':
      return 'Downloaded';
    case 'UNCHANGED':
      return 'Unchanged';
    case 'FAILED':
      return 'Download failed';
    case 'OVERRIDE':
      return 'Manual file';
  }
}

export function fileOutcomeTone(
  outcome: SourceFileStatus['outcome'],
): 'positive' | 'muted' | 'danger' | 'warning' {
  switch (outcome) {
    case 'DOWNLOADED':
      return 'positive';
    case 'UNCHANGED':
      return 'muted';
    case 'FAILED':
      return 'danger';
    case 'OVERRIDE':
      return 'warning';
  }
}
